import type {
    CommerceCustomerSession,
    CreateCommerceCustomerRequest,
    ResolveCommerceCustomerIdentityRequest,
    ResolveCommerceCustomerIdentityResponse,
    CommerceOperationOptions,
    LoginCommerceCustomerRequest,
    RequestCommerceCustomerVerificationCodeResponse,
    VerifyCommerceCustomerCodeRequest,
    CommerceCustomerRegistrationConfiguration,
    CommerceCustomerSponsor,
    CommerceCustomerAction,
} from "@teqtank/commerce-contracts";
import { CommerceRepository } from "../repositories/commerce-repository.js";
import { loginCommerceCustomerOperation } from "../operations/login-commerce-customer-operation.js";
import { logoutCommerceCustomerOperation } from "../operations/logout-commerce-customer-operation.js";
import { refreshCommerceCustomerSessionOperation } from "../operations/refresh-commerce-customer-session-operation.js";
import { requestCommerceCustomerVerificationCodeOperation } from "../operations/request-commerce-customer-verification-code-operation.js";
import { createCommerceCustomerOperation } from "../operations/create-commerce-customer-operation.js";
import { resolveCommerceCustomerIdentityOperation } from "../operations/resolve-commerce-customer-identity-operation.js";
import { verifyCommerceCustomerCodeOperation } from "../operations/verify-commerce-customer-code-operation.js";
import { resolveCommerceCustomerSession } from "./customer-session-response-resolver.js";
import { getCommerceCustomerRegistrationOperation } from "../operations/get-commerce-customer-registration-operation.js";
import { searchCommerceCustomerSponsorsOperation } from "../operations/search-commerce-customer-sponsors-operation.js";
import { getCommerceCustomerActionsOperation } from "../operations/get-commerce-customer-actions-operation.js";
import { launchCommerceCustomerActionOperation } from "../operations/launch-commerce-customer-action-operation.js";

export class CommerceCustomerRepository extends CommerceRepository {
    public async getActions(
        options: CommerceOperationOptions,
    ): Promise<readonly CommerceCustomerAction[]> {
        const response = await this.execute(
            getCommerceCustomerActionsOperation,
            {},
            options,
            "customer-session-refresh-failed",
            "Teqtank Commerce could not load account actions.",
        );
        return Object.freeze(response.actions.map((action) => Object.freeze({ ...action })));
    }
    public async launchAction(
        actionId: string,
        options: CommerceOperationOptions,
    ): Promise<string> {
        const response = await this.execute(
            launchCommerceCustomerActionOperation,
            { actionId },
            options,
            "customer-session-refresh-failed",
            "Teqtank Commerce could not open that account action.",
        );
        return response.url;
    }
    public async getRegistration(
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerRegistrationConfiguration> {
        const response = await this.execute(
            getCommerceCustomerRegistrationOperation,
            {},
            options,
            "customer-creation-failed",
            "Teqtank Commerce could not load account registration settings.",
        );
        return Object.freeze({
            ...response.registration,
            consents: Object.freeze([...response.registration.consents]),
        });
    }
    public async searchSponsors(
        search: string,
        options: CommerceOperationOptions,
    ): Promise<readonly CommerceCustomerSponsor[]> {
        const response = await this.execute(
            searchCommerceCustomerSponsorsOperation,
            { search },
            options,
            "customer-creation-failed",
            "Teqtank Commerce could not search sponsors.",
        );
        return Object.freeze(response.sponsors.map((sponsor) => Object.freeze({ ...sponsor })));
    }
    public async resolveIdentity(
        request: ResolveCommerceCustomerIdentityRequest,
        options: CommerceOperationOptions,
    ): Promise<ResolveCommerceCustomerIdentityResponse> {
        return this.execute(
            resolveCommerceCustomerIdentityOperation,
            request,
            options,
            "customer-identity-resolution-failed",
            "Teqtank Commerce could not validate the customer details.",
        );
    }
    public async create(
        request: CreateCommerceCustomerRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession> {
        const response = await this.execute(
            createCommerceCustomerOperation,
            request,
            options,
            "customer-creation-failed",
            "Teqtank Commerce could not create the customer account.",
        );
        return resolveCommerceCustomerSession(response.session);
    }
    public async login(
        request: LoginCommerceCustomerRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession> {
        const response = await this.execute(
            loginCommerceCustomerOperation,
            request,
            options,
            "customer-authentication-failed",
            "Teqtank Commerce could not authenticate the customer.",
        );
        return resolveCommerceCustomerSession(response.session);
    }
    public async requestVerificationCode(
        email: string,
        options: CommerceOperationOptions,
    ): Promise<RequestCommerceCustomerVerificationCodeResponse> {
        const response = await this.execute(
            requestCommerceCustomerVerificationCodeOperation,
            { email },
            options,
            "customer-authentication-failed",
            "Teqtank Commerce could not send the verification code.",
        );
        return Object.freeze({ ...response, expiresAt: new Date(response.expiresAt) });
    }
    public async verifyCode(
        request: VerifyCommerceCustomerCodeRequest,
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession> {
        const response = await this.execute(
            verifyCommerceCustomerCodeOperation,
            request,
            options,
            "customer-authentication-failed",
            "Teqtank Commerce could not verify the customer code.",
        );
        return resolveCommerceCustomerSession(response.session);
    }
    public async refresh(
        customerId: string | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession> {
        const request = customerId === undefined ? {} : { customerId };
        const response = await this.execute(
            refreshCommerceCustomerSessionOperation,
            request,
            options,
            "customer-session-refresh-failed",
            "Teqtank Commerce could not refresh the customer session.",
        );
        return resolveCommerceCustomerSession(response.session);
    }
    public async logout(
        customerId: string | undefined,
        options: CommerceOperationOptions,
    ): Promise<CommerceCustomerSession> {
        const request = customerId === undefined ? {} : { customerId };
        const response = await this.execute(
            logoutCommerceCustomerOperation,
            request,
            options,
            "customer-session-refresh-failed",
            "Teqtank Commerce could not end the customer session.",
        );
        return resolveCommerceCustomerSession(response.session);
    }
}
